import { Box, Button, Link, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import AuthFrame from '@/components/auth/AuthFrame';
import useAuth from '@/hooks/useAuth';
import { login, saveIdToken, toAuthApiError } from '@/services/authApi';
import { URL } from '@/types';
import { safeReturnPath } from '@/utils/auth';
import { clearPendingPassword, keepPendingPassword } from '@/utils/pendingPassword';

type LoginForm = { email: string; password: string };
/** Auth.tsx（from）と確認コード・再設定画面（email）から渡される */
type LoginState = { from?: string; email?: string } | null;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LoginState };
  const { validationRules, errorMessage } = useAuth();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit } = useForm<LoginForm>({
    defaultValues: { email: state?.email ?? '', password: '' },
    mode: 'onChange',
  });
  // ログイン画面のパスワード欄は「必須」だけ（8 文字規則は登録・再設定側で掛ける。既存パスワードを弾かないため）。
  // インラインの `rules={{ required: t(...) }}` は t() の DefaultTFuncReturn（string | null）が
  // Controller の rules 型（string | ValidationRule<boolean> | undefined）と衝突して tsc が通らないため、
  // 変数に一度受けて渡す（挙動は変わらない）
  const passwordRules = { required: t('pages:auth:messages:password_required') };

  const onSubmit = async ({ email, password }: LoginForm) => {
    setError(undefined);
    setSubmitting(true);
    try {
      const { id_token } = await login(email, password);
      saveIdToken(id_token);
      // ここまで来たら自動ログイン用に残っていたパスワードはもう要らない
      clearPendingPassword();
      navigate(safeReturnPath(state?.from), { replace: true });
    } catch (e) {
      const { code } = toAuthApiError(e);
      if (code === 'user_not_confirmed') {
        // メール未確認: 確認コード画面へ送り、到着時にコードを再送する（spec §3.7）。
        // 確認後に自動ログインできるよう、今回入力されたパスワードをメモリに保持しておく
        keepPendingPassword(email, password);
        navigate(URL.SIGN_UP_CONFIRM, { state: { email, resend: true } });
        return;
      }
      if (code === 'password_reset_required') {
        navigate(URL.FORGOT_PASSWORD, { state: { email, resetRequired: true } });
        return;
      }
      setError(errorMessage(code));
      setSubmitting(false);
    }
  };

  return (
    <AuthFrame title={t('pages:auth:login:title')} error={error}>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          <Controller
            name="email"
            control={control}
            rules={validationRules.email}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="email"
                autoComplete="email"
                label={t('pages:auth:labels:email')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={passwordRules}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                autoComplete="current-password"
                label={t('pages:auth:labels:password')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {t('pages:auth:login:submit')}
          </Button>
        </Box>
        <Stack spacing={1} alignItems="center">
          <Link component={RouterLink} to={URL.FORGOT_PASSWORD}>
            {t('pages:auth:login:forgot_password')}
          </Link>
          <Link component={RouterLink} to={URL.SIGN_UP}>
            {t('pages:auth:login:sign_up')}
          </Link>
        </Stack>
      </Box>
    </AuthFrame>
  );
}
